import Grid from '@mui/material/Grid';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import type { Task } from '../../services/api/tasksService';

interface Props {
  tasks: Task[];
}

const isLate = (t: Task): boolean => {
  if (!t.dueDate) return false;
  if (t.status === 'CLOSED' || t.status === 'PAID' || t.status === 'APPROVED') {
    return false;
  }
  return new Date(t.dueDate).getTime() < Date.now();
};

const isAtRisk = (t: Task): boolean => {
  if (!t.dueDate || isLate(t)) return false;
  const daysLeft = Math.ceil(
    (new Date(t.dueDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24)
  );
  if (daysLeft > 3) return false;
  return t.percentComplete < 80;
};

const isBlocked = (t: Task): boolean =>
  t.status === 'BLOCKED_DEPENDENCY' ||
  t.status === 'BLOCKED_SHORTAGE' ||
  t.status === 'ON_HOLD';

interface TileProps {
  label: string;
  value: number;
  color?: string;
}

function Tile({ label, value, color = 'text.primary' }: TileProps) {
  return (
    <Grid item xs={6} md={3}>
      <Card>
        <CardContent>
          <Typography variant="overline" color="text.secondary">
            {label}
          </Typography>
          <Box>
            <Typography variant="h4" sx={{ color, fontWeight: 600 }}>
              {value}
            </Typography>
          </Box>
        </CardContent>
      </Card>
    </Grid>
  );
}

export default function LookAheadSummaryTiles({ tasks }: Props) {
  const total = tasks.length;
  const lateCount = tasks.filter(isLate).length;
  const atRiskCount = tasks.filter(isAtRisk).length;
  const blockedCount = tasks.filter(isBlocked).length;

  return (
    <Grid container spacing={2}>
      <Tile label="Tasks in window" value={total} />
      <Tile label="Late" value={lateCount} color={lateCount > 0 ? 'error.main' : undefined} />
      <Tile label="At risk" value={atRiskCount} color={atRiskCount > 0 ? 'warning.main' : undefined} />
      <Tile label="Blocked" value={blockedCount} color={blockedCount > 0 ? 'error.main' : undefined} />
    </Grid>
  );
}

export { isLate, isAtRisk, isBlocked };
